<template>
  <ion-page>
    <MobileHeader></MobileHeader>
    <ion-content>
      <DesktopLocaleSelector></DesktopLocaleSelector>
      <ContentBanner
        data-test="user-banner"
        :message="welcomeMessage"
        :imagePath="require('@/assets/em-platformGuide-banner.svg')"
        imageWidthMobile="185px"
        imageMarginLeftMobile="-12px"
        :title="pageTitle"
      ></ContentBanner>
      <div class="page-bg">
        <div class="page-wrapper" id="dashboard-main-content" tabindex="0">
          <!-- Swiper -->
          <InProgressCourseCarouselVue></InProgressCourseCarouselVue>

          <!-- Call To Action -->
          <swiper
            v-if="featuredPanelStore.getFeaturedPanels.length"
            class="swiper-cards"
            :initialSlide="0"
            slidesPerView="auto"
            :spaceBetween="8"
            :slidesOffsetAfter="24"
            :slidesOffsetBefore="24"
            :navigation="true"
            :modules="modules"
          >
            <swiper-slide v-for="panel in featuredPanels" :key="panel._id">
              <FeaturePanel
                :title="panel.title"
                :description="panel.description"
                :buttonText="panel.button_text"
                :link="panel.link"
                :icon="panel.icon"
              />
            </swiper-slide>
          </swiper>
          <!--Program Carousel-->
          <ProgramCarousel
            v-if="
              featureFlagStore.isFlagEnabled(
                'release-component-program-carousel',
                false
              )
            "
            data-test="program-carousel"
            :key="programCarouselReloadKey"
          ></ProgramCarousel>

          <!-- Featured Courses -->
          <div
            v-if="
              courseStore.getCourses.length &&
              featureFlagStore.isFlagEnabled(
                'release-component-course-carousel',
                true
              )
            "
          >
            <div class="title-wrapper">
              <h5 class="page-title title-md" data-test="featured-course-title">
                {{ $t("learnHome.courseTitle") }}
              </h5>
              <div>
                <router-link :to="{ name: 'Course' }">
                  <p class="see-all-link">
                    {{ $t("learnHome.seeAllContentLink") }}
                  </p>
                </router-link>
              </div>
            </div>
            <CourseCards :show="true"></CourseCards>
          </div>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script>
import mobileLogo from "@/assets/mobile-logo.svg";
import { IonPage, IonContent } from "@ionic/vue";
import MobileHeader from "@/components/Common/MobileHeader/MobileHeader.vue";
import "swiper/css";
import "swiper/css/navigation";
import { Swiper, SwiperSlide } from "swiper/vue";
import SwiperCore, { Navigation } from "swiper";
import {
  useCourseStore,
  useParticipantStore,
  useFeatureFlagStore,
  useFeaturedPanelStore,
} from "@/store";
import ContentBanner from "@/components/Common/ContentBanner/ContentBanner.vue";
import { RouterLink } from "vue-router";
import CourseCards from "@/components/LearnHome/CourseCards.vue";
import InProgressCourseCarouselVue from "@/components/InProgressCourseCarousel/InProgressCourseCarousel.vue";
import DesktopLocaleSelector from "@/components/Common/LocaleSelector/DesktopLocaleSelector.vue";
import ProgramCarousel from "@/components/ProgramCarousel/ProgramCarousel.vue";
import FeaturePanel from "@/components/FeaturePanel/FeaturePanel.vue";

SwiperCore.use(Navigation);

export default {
  name: "DashboardHome",
  components: {
    ContentBanner,
    CourseCards,
    DesktopLocaleSelector,
    FeaturePanel,
    InProgressCourseCarouselVue,
    IonContent,
    IonPage,
    MobileHeader,
    ProgramCarousel,
    RouterLink,
    Swiper,
    SwiperSlide,
  },
  computed: {
    participantName() {
      return this.participantStore.participant?.givenName;
    },
    welcomeMessage() {
      const message = this.$t("dashboard.contentBannerWelcomeText", {
        name: this.participantName,
      });
      return this.participantStore.loading ? "" : message;
    },
    featuredPanels() {
      return this.featuredPanelStore.getFeaturedPanels;
    },
  },
  data() {
    return {
      modules: [Navigation],
      mobileLogo: mobileLogo,
      programCarouselReloadKey: 0,
      pageTitle: this.$route?.meta.title || "",
    };
  },
  setup() {
    const participantStore = useParticipantStore();
    const courseStore = useCourseStore();
    const featureFlagStore = useFeatureFlagStore();
    const featuredPanelStore = useFeaturedPanelStore();
    return {
      participantStore,
      courseStore,
      featureFlagStore,
      featuredPanelStore,
    };
  },
  mounted() {
    this.participantStore.participantRequest();
    this.courseStore.requestCourses();
    this.courseStore.requestCoursesInProgress("latestDate");
    this.featuredPanelStore.requestFeaturedPanels();
  },

  ionViewDidEnter() {
    this.pageTitle = this.$route?.meta.title || "";
    //TODO: Delete this once we move to Custom Desktop Routing Navigation
    this.courseStore.requestCoursesInProgress("latestDate");
    // Increment a random number every time the user enters the page.
    // This random number is used to reload the program carousel, so updated data is rendered via a new program carousel API request.
    this.programCarouselReloadKey++;
  },
};
</script>

<style lang="scss" scoped>
@import "@/common/theme/breakpoints.scss";
@import "@/common/theme/swiper.scss";

.page-wrapper {
  z-index: 9;
  text-align: left;
  min-height: 80vh;
  box-shadow: none;
  border-radius: unset;
}
.page-bg {
  background: var(
    --ion-color-primary-banner-bg
  ); // this is to prevent the grey corners under the banner
}
.title-wrapper {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  margin-top: 0px;
}

.swiper {
  margin-left: -24px;
  margin-right: -24px;
  margin-bottom: 26px;
  position: relative;
}

@include breakpoint(medium) {
  .page-wrapper {
    padding-left: 50px;
    padding-top: 27px;
  }
  .title-md {
    margin-bottom: 14px;
  }
}

@include breakpoint(medium) {
  .swiper :deep(.swiper-button-prev),
  .swiper :deep(.swiper-button-next) {
    height: 129px;
    top: calc(100% - 144px);
  }
}
</style>
